import React from 'react';
import { useAuthProviderV2, useRoutesProvider } from '../../../providers/provider-hooks';

/**
 * Report Controls Component
 *
 * Standardized control bar for all report pages.
 * Eliminates 180 lines of duplicated control layout (30 lines × 6 pages).
 *
 * Features:
 * - Displays report details (page info, total elements)
 * - Reset filters button
 * - Refresh button
 * - Export to Excel button (TEMPORARILY hidden for PROFESOR and ELEV roles)
 * - Optional extra controls slot (for EditionSelector, etc.)
 * - Sub `md`, uneltele de tabel (reset, reincarcare, export) NU se randeaza:
 *   la latimea aia raportul e rezumat, nu tabel. Raman doar `extraControls`.
 *
 * Usage:
 * ```tsx
 * <ReportControls
 *   pagination={adapters.pagination}
 *   isLoading={report.isLoading}
 *   onRefresh={report.refetch}
 *   onResetFilters={report.resetFilters}
 *   onExport={handleExport}
 * />
 * ```
 */

// Import TypeScript table components
import ReportDetails from './ReportDetails';
import RefreshButton from './RefreshButton';
import FiltersResetButton from './FiltersResetButton';
import { Button } from '../../../components/ui/button';

export interface ReportControlsProps {
    /** Pagination data in old JSX format */
    pagination: {
        pageNumber: number;
        totalPages: number;
        totalElements: number;
        pageSize: number;
    };

    /** Loading state (disables all buttons) */
    isLoading: boolean;

    /** Refresh/refetch handler */
    onRefresh: () => void;

    /** Reset filters handler */
    onResetFilters: () => void;

    /** Export to Excel handler */
    onExport: () => Promise<void>;

    /** Optional extra controls (e.g., EditionSelector) - rendered first */
    extraControls?: React.ReactNode;

    /** Show export button (default: true) */
    showExport?: boolean;

    /**
     * Starea de eroare a raportului: exportul si resetarea filtrelor se
     * dezactiveaza, dar VALORILE filtrelor raman — „Reîncearcă" trebuie sa refaca
     * exact aceeasi interogare. Exportul mai ales: peste o cerere picata ar
     * produce un fisier care afirma zero.
     *
     * Butonul de reincarcare NU se dezactiveaza: e singura actiune care are sens
     * in starea aia, si e aceeasi cu „Reîncearcă" din corpul raportului.
     */
    disabled?: boolean;
}

/**
 * Standardized control bar with all common report actions
 *
 * Renders 4 standard controls + optional extras:
 * 1. Extra controls (if provided)
 * 2. ReportDetails - shows "Page X of Y (Z total)"
 * 3. FiltersResetButton - clears all filters
 * 4. RefreshButton - refetches data
 * 5. ExportButton - downloads Excel file
 *
 * Layout: Responsive flex container (stacks on mobile, row on desktop)
 */
export function ReportControls({
    pagination,
    isLoading,
    onRefresh,
    onResetFilters,
    onExport,
    extraControls,
    showExport = true,
    disabled = false
}: ReportControlsProps) {

    // TEMPORARY: Hide export button for PROFESOR and ELEV roles (2025-10-28)
    // To re-enable: Remove lines 102-105 and this comment
    const { userInfo } = useAuthProviderV2();
    const { ROLES } = useRoutesProvider();
    const canExport = userInfo?.role && ![ROLES.PROFESOR, ROLES.ELEV].includes(userInfo.role);

    return (
        <div className="flex-none flex w-full flex-col space-y-4 pb-4 lg:flex-row lg:items-end lg:justify-end lg:space-x-4 lg:space-y-0">
            {/* Extra controls (EditionSelector, etc.) — singurele care raman si pe mobil */}
            {extraControls}

            {/*
              Uneltele tabelului: sub `md` nu se randeaza deloc, fiindca sub `md`
              nu exista tabel (vezi `ReportPageLayout`). Aici e si singurul loc
              din care se poate porni exportul Excel — deci „zero export pe
              mobil" e o consecinta a `hidden`, nu o a doua regula de intretinut.

              `md:contents` (nu `md:block`): de la 768px in sus wrapper-ul
              dispare din layout, iar cei patru copii raman copii DIRECTI ai
              flex-ului de mai sus. Fara asta, `lg:space-x-4` si `w-full`-ul din
              ReportDetails s-ar aplica altfel si desktopul s-ar muta.
            */}
            <div className="hidden md:contents">
                {/* Report info */}
                <ReportDetails pagination={pagination} />

                {/* Reset filters */}
                <FiltersResetButton
                    handleRefresh={onResetFilters}
                    disabled={isLoading || disabled}
                />

                {/* Refresh data */}
                <RefreshButton
                    handleRefresh={onRefresh}
                    disabled={isLoading}
                />

                {/* Export to Excel - TEMPORARILY hidden for PROFESOR and ELEV */}
                {showExport && onExport && canExport && (
                    <Button onClick={onExport} disabled={isLoading || disabled} className="w-full sm:w-auto">
                        <span className="pl-2">Export</span>
                    </Button>
                )}
            </div>
        </div>
    );
}
